import React, { useState } from 'react';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
  Box,
} from '@mui/material';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';

import { Tab } from '../../@types/State';
import { useAppState } from '../../context/AppState';
import { useIntelState } from '../../context/IntelState';
import { resolveDragonSVG, resolveRitualSVG } from '../../Effects';
import useBuildingBookNames from '../../hooks/useBuildingBookNames';
import EnemySwitcher from '../Common/EnemySwitcher';
import IntelGridTabs from '../Intel/components/IntelGridTabs';
import Legend from '../Intel/components/Legend';
import ArmyGrid from '../Intel/gridViews/ArmyGrid';
import EnemyOpsGrid from '../Intel/gridViews/EnemyOpsGrid';
import KingdomOpsGrid from '../Intel/gridViews/KingdomOpsGrid';
import MainGrid from '../Intel/gridViews/MainGrid';
import WarGrid from '../Intel/gridViews/WarGrid';

const Intel = () => {
  const location = useLocation();
  const { intelState } = useIntelState();
  const { appDispatch } = useAppState();
  const matchesSmallWidth = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );
  const [currentTab, setCurrentTab] = useState<Tab | 'gains'>('overview');
  const [zoom, setZoom] = useState(isMobile ? 75 : 100);
  const isViewingEnemy: boolean =
    location.pathname === '/enemy' || location.pathname === '/enemyops';
  const [bookNames, buildNames] = useBuildingBookNames();
  const dataSet = isViewingEnemy ? intelState.enemyData : intelState.ownData;

  const getView = () => {
    switch (currentTab) {
      case 'ops':
        return isViewingEnemy ? <EnemyOpsGrid /> : <KingdomOpsGrid />;
      case 'gains':
        return <WarGrid />;
      case 'armies':
        return (
          <ArmyGrid
            dataSet={dataSet?.provinces}
            isViewingEnemy={isViewingEnemy}
          />
        );
      default:
        return (
          <MainGrid
            dataSet={dataSet?.provinces}
            plan={isViewingEnemy ? intelState.enemyPlan : intelState.ownPlan}
            currentTab={currentTab}
            isViewingEnemy={isViewingEnemy}
            zoom={zoom}
          />
        );
    }
  };

  const ownKd = intelState.ownData ?? undefined;
  const enemyKd = intelState.enemyData ?? undefined;

  return (
    <Box
      className='Home'
      sx={{
        mt: isMobile ? 0 : 2,
      }}
    >
      <Box
        sx={{
          height: '50px',
          display: { xs: 'flex' },
          alignItems: 'center',
          justifyContent: ['space-between', 'flex-start'],
          marginX: 2,
        }}
      >
        {isViewingEnemy && <EnemySwitcher />}
        <Typography
          variant='body1'
          component='span'
          sx={{
            maxWidth: isMobile ? (isViewingEnemy ? 190 : 250) : '100%',
            ml: 1,
          }}
        >
          {isViewingEnemy ? enemyKd?.kingdomName : ownKd?.kingdomName}
        </Typography>
        {isMobile && (
          <Box>
            <IconButton
              aria-label='zoom in'
              onClick={() => {
                const newZoom = Math.min(zoom + 25, 100);
                setZoom(newZoom);
                appDispatch({
                  type: 'SET_ZOOM',
                  payload: newZoom === 75 ? 'default' : 'in',
                });
              }}
              disabled={zoom === 100}
              sx={{ padding: 0, mr: 1 }}
              size='large'
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              aria-label='zoom out'
              onClick={() => {
                const newZoom = Math.max(zoom - 25, 50);
                setZoom(newZoom);
                appDispatch({
                  type: 'SET_ZOOM',
                  payload: newZoom === 75 ? 'default' : 'out',
                });
              }}
              disabled={zoom === 50}
              sx={{ padding: 0 }}
              size='large'
            >
              <ZoomOutIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <div
        style={{
          zoom: `${zoom || 100}%`,
        }}
      >
        <Box
          sx={{
            mx: 2,
            mt: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {dataSet?.kdEffects?.ritual && (
            <>
              {resolveRitualSVG(
                dataSet?.kdEffects?.ritual,
                dataSet?.kdEffects?.ritualDuration
              )}
              <span style={{ marginRight: '10px', marginLeft: '5px' }}>
                {dataSet?.kdEffects?.ritual}
                {', '}
                {dataSet?.kdEffects?.ritualDuration} ticks{', '}
                {dataSet?.kdEffects?.ritualEff}%
              </span>
            </>
          )}
          {dataSet?.kdEffects?.dragon && (
            <>
              {resolveDragonSVG(dataSet?.kdEffects?.dragon)}
              <span style={{ marginRight: '10px', marginLeft: '5px' }}>
                {dataSet?.kdEffects?.dragon}
                {', '}
                {48 - (dataSet.kdEffects.dragonDuration ?? 0)} ticks remaining
              </span>
            </>
          )}
        </Box>
        <IntelGridTabs
          currentTab={currentTab}
          handleChangeTab={(e: any, newValue: any) => setCurrentTab(newValue)}
          hasBookNames={!!bookNames}
          hasBuildingNames={!!buildNames}
        />
        <Box
          style={{ minHeight: 200 }}
          sx={{
            mx: 1,
            mb: 2,
          }}
        >
          <div style={{ width: '100%' }}>{getView()}</div>
        </Box>
      </div>
      <Legend useBlocks={matchesSmallWidth} />
    </Box>
  );
};

export default Intel;
